import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import {FilterIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, CounterLabel, IconButton} from '@primer/react'
import {type FC, useCallback, useMemo, useState} from 'react'
import {getEntitySeverity, getEntitySource, SEVERITY_ORDER} from './entity-helpers'
import styles from './FindingsFilterBar.module.css'
import type {FindingEntity} from './types'

export const useFindingsFilter = (openFindings: FindingEntity[], ignoredFindings: FindingEntity[]) => {
  const [selectedVisibility, setSelectedVisibility] = useState<'open' | 'resolved'>('open')
  const [selectedSeverities, setSelectedSeverities] = useState<Set<RuleSeverity>>(() => new Set())
  const [selectedSources, setSelectedSources] = useState<Set<string>>(() => new Set())

  const allFindings = useMemo(() => [...openFindings, ...ignoredFindings], [openFindings, ignoredFindings])

  const availableSeverities = useMemo(() => {
    const severities = new Set<RuleSeverity>()
    for (const f of allFindings) {
      const s = getEntitySeverity(f)
      if (s) severities.add(s)
    }
    return SEVERITY_ORDER.filter(s => severities.has(s))
  }, [allFindings])

  const availableSources = useMemo(() => {
    const sources = new Set<string>()
    for (const f of allFindings) {
      sources.add(getEntitySource(f))
    }
    return [...sources].sort()
  }, [allFindings])

  const handleSeverityToggle = useCallback((severity: RuleSeverity) => {
    setSelectedSeverities(prev => {
      const next = new Set(prev)
      if (next.has(severity)) next.delete(severity)
      else next.add(severity)
      return next
    })
  }, [])

  const handleSourceToggle = useCallback((source: string) => {
    setSelectedSources(prev => {
      const next = new Set(prev)
      if (next.has(source)) next.delete(source)
      else next.add(source)
      return next
    })
  }, [])

  const applyFilters = useCallback(
    (items: FindingEntity[]) => {
      return items.filter(f => {
        if (selectedSeverities.size > 0) {
          const s = getEntitySeverity(f)
          if (!s || !selectedSeverities.has(s)) return false
        }
        if (selectedSources.size > 0) {
          if (!selectedSources.has(getEntitySource(f))) return false
        }
        return true
      })
    },
    [selectedSeverities, selectedSources],
  )

  const displayedFindings = useMemo(
    () => applyFilters(selectedVisibility === 'open' ? openFindings : ignoredFindings),
    [applyFilters, selectedVisibility, openFindings, ignoredFindings],
  )

  return {
    selectedVisibility,
    handleVisibilityChange: setSelectedVisibility,
    availableSeverities,
    availableSources,
    selectedSeverities,
    selectedSources,
    handleSeverityToggle,
    handleSourceToggle,
    displayedFindings,
  }
}

export type FindingsFilterState = ReturnType<typeof useFindingsFilter>

interface FindingsFilterBarProps extends Omit<FindingsFilterState, 'displayedFindings'> {
  openCount: number
  resolvedCount: number
  onDismissAll: () => void
  onReopenAll: () => void
}

const SEVERITY_LABELS: Partial<Record<RuleSeverity, string>> = {
  failure: 'Failure',
  critical: 'Critical',
  high: 'High',
  error: 'Error',
  medium: 'Medium',
  warning: 'Warning',
  low: 'Low',
  note: 'Note',
  nit: 'Nit',
  notice: 'Notice',
  none: 'None',
}

export const FindingsFilterBar: FC<FindingsFilterBarProps> = ({
  openCount,
  resolvedCount,
  selectedVisibility,
  availableSeverities,
  availableSources,
  selectedSeverities,
  selectedSources,
  handleVisibilityChange,
  handleSeverityToggle,
  handleSourceToggle,
  onDismissAll,
  onReopenAll,
}) => {
  const activeFilterCount = selectedSeverities.size + selectedSources.size

  return (
    <div className={styles.filterBar}>
      <h2 className={styles.heading}>Findings</h2>
      <div className={styles.buttons}>
        <Button
          variant="invisible"
          onClick={() => handleVisibilityChange('open')}
          aria-pressed={selectedVisibility === 'open'}
        >
          Open <CounterLabel>{openCount}</CounterLabel>
        </Button>
        <Button
          variant="invisible"
          onClick={() => handleVisibilityChange('resolved')}
          aria-pressed={selectedVisibility === 'resolved'}
        >
          Resolved <CounterLabel>{resolvedCount}</CounterLabel>
        </Button>
        <ActionMenu>
          <ActionMenu.Anchor>
            <Button
              variant="invisible"
              leadingVisual={FilterIcon}
              aria-label={`Filter findings${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
            >
              {activeFilterCount > 0 && <CounterLabel>{activeFilterCount}</CounterLabel>}
            </Button>
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              {availableSeverities.length > 0 && (
                <ActionList.Group selectionVariant="multiple">
                  <ActionList.GroupHeading>Severity</ActionList.GroupHeading>
                  {availableSeverities.map(severity => (
                    <ActionList.Item
                      key={severity}
                      selected={selectedSeverities.has(severity)}
                      onSelect={() => handleSeverityToggle(severity)}
                    >
                      {SEVERITY_LABELS[severity] ?? severity}
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
              )}
              {availableSeverities.length > 0 && availableSources.length > 0 && <ActionList.Divider />}
              {availableSources.length > 0 && (
                <ActionList.Group selectionVariant="multiple">
                  <ActionList.GroupHeading>Sources</ActionList.GroupHeading>
                  {availableSources.map(source => (
                    <ActionList.Item
                      key={source}
                      selected={selectedSources.has(source)}
                      onSelect={() => handleSourceToggle(source)}
                    >
                      {source}
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={KebabHorizontalIcon} variant="invisible" aria-label="More actions" />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end">
            <ActionList>
              {selectedVisibility === 'open' && (
                <ActionList.Item onSelect={onDismissAll} disabled={openCount === 0}>
                  Dismiss all findings
                </ActionList.Item>
              )}
              {selectedVisibility === 'resolved' && (
                <ActionList.Item onSelect={onReopenAll} disabled={resolvedCount === 0}>
                  Reopen all findings
                </ActionList.Item>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </div>
  )
}
